// Account-scoped coordinator for the server-owned exchange. It never derives
// value from a partial collection, applies receipt deltas, or retries with a
// fresh request ID after an uncertain response.
const STORAGE_KEY = 'moonfall.cardPacks.exchange.v1';
const safe = value => Number.isSafeInteger(value) && value >= 0;
const statuses = new Set(['active', 'completed', 'interrupted']);

function invalid(message) {
  return Object.assign(new Error(message), { exchangeProtocolFailure: true });
}
function readQuote(value, accountId) {
  if (!value || value.schemaVersion !== 1 || value.accountId !== accountId || typeof value.ready !== 'boolean')
    throw invalid('duplicate_quote_invalid');
  if (!value.ready) {
    if (!['catalog', 'account', 'source'].includes(value.reason) || 'totalValue' in value) throw invalid('duplicate_quote_invalid');
  } else if (!safe(value.totalValue) || !safe(value.revision)
    || value.packsAvailable !== Math.floor(value.totalValue / 20) || value.remainderValue !== value.totalValue % 20) {
    throw invalid('duplicate_quote_invalid');
  }
  return Object.freeze({ ...value });
}
function readOperation(value, accountId) {
  if (!value || value.schemaVersion !== 1 || value.accountId !== accountId || !statuses.has(value.status)
    || typeof value.operationId !== 'string' || !value.operationId || typeof value.requestId !== 'string'
    || !/^[A-Za-z0-9_-]{16,96}$/.test(value.requestId)
    || ![value.targetValue, value.quotedValue, value.quotedRevision, value.consumedValue, value.packsGranted, value.packCount].every(safe)
    || value.targetValue !== Math.floor(value.quotedValue / 20) * 20 || value.targetValue < 20
    || value.consumedValue !== value.packsGranted * 20 || value.consumedValue > value.targetValue
    || value.duplicateValuePerPack !== 20 || value.normalDuplicateValue !== 1 || value.stellarDuplicateValue !== 20
    || (value.status === 'completed' && value.consumedValue !== value.targetValue)
    || (value.status !== 'completed' && value.consumedValue >= value.targetValue)
    || (value.status === 'interrupted' && !['inventory_changed', 'account_banned'].includes(value.reason)))
    throw invalid('duplicate_operation_invalid');
  return Object.freeze({ ...value });
}
function validRequest(request) {
  return request && /^[A-Za-z0-9_-]{16,96}$/.test(request.requestId)
    && safe(request.expectedRevision) && safe(request.expectedValue) && request.expectedValue >= 20;
}
function defaultWait(ms, signal) {
  return new Promise(resolve => {
    if (signal.aborted) return resolve();
    let timer;
    const finish = () => {
      clearTimeout(timer); signal.removeEventListener('abort', finish);
      globalThis.document?.removeEventListener('visibilitychange', visible); resolve();
    };
    const visible = () => { if (globalThis.document?.visibilityState !== 'hidden') finish(); };
    const elapsed = () => {
      if (globalThis.document?.visibilityState === 'hidden') globalThis.document.addEventListener('visibilitychange', visible);
      else finish();
    };
    timer = setTimeout(elapsed, ms);
    signal.addEventListener('abort', finish, { once: true });
  });
}

export function createPackExchangeSession({ api, source, storage, onState, onCommitted = (_operation, _accountId) => {},
  requestId = () => globalThis.crypto.randomUUID(), wait = defaultWait }) {
  if (!source || typeof source !== 'string' || typeof onState !== 'function'
    || ['ensureTotals', 'getExchange', 'startExchange'].some(name => typeof api?.[name] !== 'function'))
    throw invalid('duplicate_session_configuration');
  let context = null, state = null, pending = null;
  const owns = owner => context === owner && !owner.signal.aborted;
  const storageKey = accountId => `${STORAGE_KEY}:${encodeURIComponent(source)}:${encodeURIComponent(accountId)}`;
  function emit(owner, phase, fields = {}) {
    if (!owns(owner)) return;
    state = Object.freeze({ phase, accountId: owner.accountId, quote: state?.quote ?? null,
      operation: state?.operation ?? null, error: null, ...fields });
    onState(state);
  }
  function loadPending(accountId) {
    try {
      const record = JSON.parse(storage?.getItem(storageKey(accountId)) || storage?.getItem(STORAGE_KEY) || 'null');
      if (record?.source === source && record.accountId === accountId && validRequest(record.request)) return record.request;
    } catch (_) { /* In-memory/server recovery remains available without storage. */ }
    return null;
  }
  function remember(owner, request) {
    if (!owns(owner)) return;
    pending = request;
    try {
      if (request) storage?.setItem(storageKey(owner.accountId), JSON.stringify({ source, accountId: owner.accountId, request }));
      else {
        storage?.removeItem(storageKey(owner.accountId));
        // Read and retire the first version's single-slot record only for its
        // owner. Independent accounts/deployments now keep separate requests.
        const old = JSON.parse(storage?.getItem(STORAGE_KEY) || 'null');
        if (old?.source === source && old.accountId === owner.accountId) storage?.removeItem(STORAGE_KEY);
      }
    } catch (_) { /* Never lose the live request because storage is unavailable. */ }
  }
  const delay = (owner, failures = 0) => wait(Math.min(30000, 1000 * 2 ** Math.min(failures, 5)), owner.signal);
  // A refusal that a retry cannot change stops the session (packs are off in
  // the visitor's country: the server's region gate).
  function fatal(error) {
    return error?.exchangeProtocolFailure || /Not authenticated|account_banned|packs_unavailable_in_region|duplicate_request_conflict|duplicate_request_invalid|duplicate_session_auth_changed/.test(String(error?.message || error));
  }
  async function latestOperation(owner) {
    let failures = 0;
    while (owns(owner)) {
      try {
        const latest = await api.getExchange(undefined, { signal: owner.signal });
        if (!owns(owner)) return null;
        return latest ? readOperation(latest, owner.accountId) : null;
      } catch (error) {
        if (!owns(owner)) return null;
        if (fatal(error)) throw error;
        emit(owner, 'reconnecting', { error });
        await delay(owner, ++failures);
      }
    }
    return null;
  }
  async function freshQuote(owner) {
    let failures = 0;
    while (owns(owner)) {
      try {
        const quote = readQuote(await api.ensureTotals({ signal: owner.signal }), owner.accountId);
        if (!owns(owner)) return null;
        if (quote.ready) return quote;
        emit(owner, 'preparing', { quote });
        failures = 0;
      } catch (error) {
        if (!owns(owner)) return null;
        if (fatal(error)) throw error;
        emit(owner, 'reconnecting', { error }); failures += 1;
      }
      await delay(owner, failures);
    }
    return null;
  }
  async function follow(owner, first) {
    let next = first, failures = 0, seenValue = -1;
    while (owns(owner)) {
      try {
        const operation = readOperation(next || await api.getExchange(pending?.operationId, { signal: owner.signal }), owner.accountId);
        if (!owns(owner)) return;
        if (pending && (operation.requestId !== pending.requestId || operation.quotedRevision !== pending.expectedRevision
          || operation.quotedValue !== pending.expectedValue || (pending.operationId && operation.operationId !== pending.operationId)))
          throw invalid('duplicate_operation_mismatch');
        if (operation.consumedValue < seenValue) throw invalid('duplicate_operation_regressed');
        remember(owner, { requestId: operation.requestId, expectedRevision: operation.quotedRevision,
          expectedValue: operation.quotedValue, operationId: operation.operationId });
        emit(owner, 'exchanging', { operation });
        if (operation.consumedValue > 0 && operation.consumedValue !== seenValue) {
          // The consumer refreshes/invalidate its authoritative inventory. These
          // are cumulative server facts, never another batch of local debits.
          await onCommitted(operation, owner.accountId);
          if (!owns(owner)) return;
          seenValue = operation.consumedValue;
        }
        if (operation.status !== 'active') {
          const quote = await freshQuote(owner);
          if (!owns(owner)) return;
          remember(owner, null);
          emit(owner, operation.status, { operation, quote });
          return;
        }
        failures = 0;
      } catch (error) {
        if (!owns(owner)) return;
        if (fatal(error)) throw error;
        emit(owner, 'reconnecting', { error }); failures += 1;
      }
      next = null;
      await delay(owner, failures);
    }
  }
  async function submit(owner) {
    let failures = 0;
    while (owns(owner) && pending) {
      // Only these original consent terms may be sent. operationId is a local
      // recovery hint, not an extra public mutation argument.
      const { requestId: id, expectedRevision, expectedValue } = pending;
      try {
        const operation = await api.startExchange({ requestId: id, expectedRevision, expectedValue }, { signal: owner.signal });
        if (!owns(owner)) return;
        await follow(owner, operation);
        return;
      } catch (error) {
        if (!owns(owner)) return;
        const message = String(error?.message || error);
        if (/duplicate_quote_changed|duplicate_quote_preparing|duplicate_value_insufficient|duplicate_exchange_active/.test(message)) {
          remember(owner, null);
          const latest = await latestOperation(owner);
          if (!owns(owner)) return;
          if (latest?.status === 'active') { await follow(owner, latest); return; }
          const quote = await freshQuote(owner);
          emit(owner, 'quote-changed', { quote, operation: null });
          return;
        }
        if (fatal(error)) throw error;
        emit(owner, 'reconnecting', { error }); failures += 1;
        await delay(owner, failures);
      }
    }
  }
  async function protect(owner, work) {
    try { await work(); }
    catch (error) { if (owns(owner)) emit(owner, 'error', { error }); }
  }
  function close() {
    context?.abort.abort();
    context = null;
  }
  async function open(accountId) {
    close();
    const abort = new AbortController();
    const owner = { accountId, abort, signal: abort.signal };
    context = owner; state = null; pending = loadPending(accountId);
    emit(owner, 'loading');
    await protect(owner, async () => {
      // An accepted operation runs without this view. Inspect it before
      // interpreting a ready aggregate as permission for another confirmation.
      const latest = await latestOperation(owner);
      if (!owns(owner)) return;
      if (latest) readOperation(latest, accountId);
      if (latest?.status === 'active') {
        // Another tab can have started a newer exchange after this saved
        // request finished. Follow the currently active account operation;
        // replaying the old terminal one must not make the screen look idle.
        if (pending && pending.requestId !== latest.requestId) remember(owner, null);
        await follow(owner, latest);
        return;
      }
      if (pending) {
        if (latest?.requestId === pending.requestId) await follow(owner, latest);
        else await submit(owner);
        return;
      }
      if (latest?.consumedValue > 0) {
        await onCommitted(latest, owner.accountId);
        if (!owns(owner)) return;
      }
      const quote = await freshQuote(owner);
      emit(owner, 'ready', { quote, operation: null });
    });
  }
  async function confirm(quote) {
    const owner = context;
    if (!owner || !owns(owner) || !['ready', 'completed', 'interrupted', 'quote-changed'].includes(state?.phase)) return;
    await protect(owner, async () => {
      const approved = readQuote(quote, owner.accountId);
      if (!approved.ready || !state.quote?.ready || approved.revision !== state.quote.revision || approved.totalValue !== state.quote.totalValue)
        throw invalid('duplicate_quote_changed');
      if (approved.packsAvailable < 1) throw invalid('duplicate_value_insufficient');
      const request = { requestId: requestId(), expectedRevision: approved.revision, expectedValue: approved.totalValue };
      if (!validRequest(request)) throw invalid('duplicate_request_invalid');
      remember(owner, request);
      emit(owner, 'exchanging', { operation: null });
      await submit(owner);
    });
  }
  return { open, close, confirm };
}
